import { faEye, faEyeSlash } from '@fortawesome/sharp-solid-svg-icons';
import { FC, memo, useCallback, useMemo } from 'react';

import Typography from '@/components/typography';
import { AcresFeatureFlags } from '@/constants/constants';
import { customizeIconMap, PointIds } from '@/features/customizeMyLand/config';
import { CustomizePoint } from '@/features/customizeMyLand/customizePoint';
import {
  CustomizeFeature,
  useCustomizeLandContext,
} from '@/features/customizeMyLand/provider';
import { hideFeature } from '@/features/customizeMyLand/util';
import { Card } from '@/features/selectionsPanel/selectionCard/baseCard/card';
import { CardCloseButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardCloseButton';
import { CardIconButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardIconButton';
import { useToolbarContext } from '@/features/toolbar/provider';
import { useMapZoomLevel } from '@/hooks/map-box/useMapZoomLevel';
import { useInitializeContext } from '@/providers/InitializeProvider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

import { useGenericSelectionPanelFunctions } from '../../hooks/useGenericSelectionPanelFunctions';

type Props = {
  feature: CustomizeFeature;
};

const CustomizationCard: FC<Props> = ({ feature }) => {
  const { mapInstance: map } = useMapboxContext();
  const { goToGeometryLocation } = useGenericSelectionPanelFunctions();
  const { drawTool, onDrawDelete, setActiveFeature, updateDrawFeature } =
    useCustomizeLandContext();
  const { getFeature } = useInitializeContext();
  const isToolbarActive = getFeature(AcresFeatureFlags.TEMP_TOOL_TOP_BAR);
  const {
    drawTool: toolbarDrawTool,
    updateDrawFeature: updateToolbarDrawFeature,
    onDrawDelete: onDrawDeleteToolbar,
  } = useToolbarContext();
  const { mapZoomLevel } = useMapZoomLevel(map);

  const handleClose = useCallback(() => {
    if (isToolbarActive) {
      onDrawDeleteToolbar({ features: feature });
    } else {
      onDrawDelete({ features: feature });
    }
  }, [feature, isToolbarActive, onDrawDelete, onDrawDeleteToolbar]);

  const handleToggleVisible = useCallback(() => {
    const drawToolToUse = isToolbarActive ? toolbarDrawTool : drawTool;
    const updateFunc = isToolbarActive
      ? updateToolbarDrawFeature
      : updateDrawFeature;
    hideFeature(
      feature,
      drawToolToUse,
      !feature?.properties?.hidden,
      updateFunc
    );
  }, [
    drawTool,
    feature,
    isToolbarActive,
    toolbarDrawTool,
    updateDrawFeature,
    updateToolbarDrawFeature,
  ]);

  const handleOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      goToGeometryLocation(feature, mapZoomLevel);
      setActiveFeature(feature);
    },
    [feature, goToGeometryLocation, setActiveFeature, mapZoomLevel]
  );

  const Icon = customizeIconMap[feature?.properties?.toolId as PointIds];

  const isPoint = useMemo(() => {
    if (!feature?.properties?.toolId) return false;
    return Object.values(PointIds).includes(
      feature?.properties?.toolId as PointIds
    );
  }, [feature?.properties?.toolId]);

  return (
    <Card onClick={handleOnClick}>
      <div className="flex items-center">
        {isPoint ? (
          <CustomizePoint
            isReadOnly
            activeFeature={null}
            buttonClassName="w-5 h-5 !max-w-[20px] !max-h-[20px]"
            className="h-5 w-5"
            map={map}
            point={feature}
          />
        ) : (
          <div className="h-5 [&>*]:max-h-[20px] [&>*]:min-h-[20px] [&>*]:min-w-[20px] [&>*]:max-w-[20px]">
            {Icon}
          </div>
        )}
        <Typography
          className="ml-3 flex-grow overflow-hidden text-ellipsis whitespace-nowrap"
          variant="button2"
        >
          {feature.properties?.name}
        </Typography>
        <CardIconButton
          className="ml-auto"
          icon={!feature?.properties?.hidden ? faEye : faEyeSlash}
          onClick={handleToggleVisible}
        />
        <CardCloseButton onClick={handleClose} />
      </div>
    </Card>
  );
};

export default memo(CustomizationCard);
